import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { apiPost, apiPut } from "../../utils/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { AdminFormDialog, AdminField } from "./AdminFormDialog";
import { Input, Textarea } from "../ui/input";

export type FaqFormState = {
  question: string;
  questionEn: string;
  answer: string;
  answerEn: string;
  sortOrder: string;
  isActive: boolean;
};

export const EMPTY_FAQ: FaqFormState = {
  question: "", questionEn: "", answer: "", answerEn: "",
  sortOrder: "", isActive: true,
};

type Props = {
  open: boolean;
  editing: any | null;
  onClose: () => void;
  onSaved: () => void;
};

const FORM_ID = "faq-form";

export const AdminFaqModal: React.FC<Props> = ({ open, editing, onClose, onSaved }) => {
  const { t } = useLanguage();
  const [localeTab, setLocaleTab] = useState<"fr" | "en">("fr");

  const schema = Yup.object({
    question: Yup.string().trim().required(t.common.required),
    answer: Yup.string().trim().required(t.common.required),
    questionEn: Yup.string(),
    answerEn: Yup.string(),
    sortOrder: Yup.number().typeError("Nombre invalide").min(0).nullable().transform((v, o) => (o === "" ? null : v)),
    isActive: Yup.boolean(),
  });

  const initial: FaqFormState = editing ? {
    question: editing.question || "",
    questionEn: editing.questionEn || "",
    answer: editing.answer || "",
    answerEn: editing.answerEn || "",
    sortOrder: editing.sortOrder != null ? String(editing.sortOrder) : "",
    isActive: editing.isActive !== false,
  } : EMPTY_FAQ;

  const isFr = localeTab === "fr";

  return (
    <Formik
      initialValues={initial}
      validationSchema={schema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        const payload = {
          question: values.question.trim(),
          questionEn: values.questionEn.trim() || null,
          answer: values.answer.trim(),
          answerEn: values.answerEn.trim() || null,
          sortOrder: values.sortOrder !== "" ? Number(values.sortOrder) : undefined,
          isActive: values.isActive,
        };
        try {
          if (editing) await apiPut(`/faqs/${editing.id}`, payload);
          else await apiPost("/faqs", payload);
          toast.success(t.common.saveSuccess);
          onSaved();
          onClose();
        } catch (e: any) {
          toast.error(e?.response?.data?.reason || e?.messageText || t.common.error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => (
        <AdminFormDialog
          open={open}
          onClose={onClose}
          title={editing ? t.admin.editFaq : t.admin.newFaq}
          subtitle={t.admin.pageContent}
          testId="faq-modal"
          formId={FORM_ID}
          submitLabel={editing ? t.admin.save : t.admin.create}
          isSubmitting={isSubmitting}
          showLocaleTabs
          localeTab={localeTab}
          onLocaleChange={setLocaleTab}
        >
          <Form id={FORM_ID} className="grid gap-4 pb-4">
            <AdminField
              label={isFr ? t.admin.faqQuestion : t.admin.faqQuestionEn}
              required={isFr}
              error={isFr && touched.question ? errors.question : undefined}
            >
              <Field
                as={Input}
                name={isFr ? "question" : "questionEn"}
                data-testid="faq-question"
              />
            </AdminField>
            <AdminField
              label={isFr ? t.admin.faqAnswer : t.admin.faqAnswerEn}
              required={isFr}
              error={isFr && touched.answer ? errors.answer : undefined}
            >
              <Field
                as={Textarea}
                name={isFr ? "answer" : "answerEn"}
                data-testid="faq-answer"
                rows={5}
              />
            </AdminField>
            <div className="grid grid-cols-2 gap-4">
              <AdminField label={t.admin.faqOrder}>
                <Field as={Input} id="faq-order" name="sortOrder" type="number" min={0} />
              </AdminField>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={values.isActive}
                    onChange={(e) => setFieldValue("isActive", e.target.checked)}
                  />
                  {values.isActive ? t.admin.active : t.admin.inactive}
                </label>
              </div>
            </div>
          </Form>
        </AdminFormDialog>
      )}
    </Formik>
  );
};
